import React, { useState, useEffect, useRef } from 'react';
import authService from '../services/authService';

export default function ForgotPasswordScreen({ onBack, onSendReset }) {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [backgroundImage, setBackgroundImage] = useState(null);
  const otpInputRefs = useRef([]);

  // Initialize refs array
  if (otpInputRefs.current.length !== 6) {
    otpInputRefs.current = Array(6).fill(null).map((_, i) => otpInputRefs.current[i] || React.createRef());
  }

  // Fetch login background from API for this business (ensures per-business isolation)
  useEffect(() => {
    const businessId = localStorage.getItem('businessId');
    if (businessId) {
      authService.getBusinessBackgroundImage(businessId).then((img) => {
        if (img) {
          setBackgroundImage(img);
        } else {
          setBackgroundImage(null);
        }
      }).catch(() => {
        setBackgroundImage(null);
      });
    }
  }, []);

  // STEP 1: Send OTP
  const handleSendOTP = async () => {
    setError('');
    setSuccess('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_URL}/auth/forgot-password-step1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.emailNotFound) {
          setError('This email is not registered. Please sign up first.');
        } else if (data.needsVerification) {
          setError('Please verify your email address first before resetting password.');
        } else {
          setError(data.error || 'Failed to send OTP');
        }
        setIsLoading(false);
        return;
      }

      // The server gives the same answer for every address (it no longer says whether an
      // email is registered), so its message is shown as it is.
      setSuccess(data.message || 'If an account exists for this email, a code has been sent. Please check your inbox.');
      setShowOtpInput(true);


      // Focus first OTP input after a short delay
      setTimeout(() => {
        if (otpInputRefs.current[0]) {
          otpInputRefs.current[0].focus();
        }
      }, 100);

    } catch (error) {
      console.error('❌ Send OTP error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Only take last character
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    const otpString = newOtp.join('');
    if (otpString.length === 6) {
      handleVerifyOTP(otpString);
    }
  };

  // Handle backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newOtp = pastedData.split('').concat(['', '', '', '', '', '']).slice(0, 6);
      setOtp(newOtp);
      
      // Focus the next empty input or last input
      const nextIndex = Math.min(pastedData.length, 5);
      otpInputRefs.current[nextIndex]?.focus();

      // Auto-submit if all 6 digits pasted
      if (pastedData.length === 6) {
        handleVerifyOTP(pastedData);
      }
    }
  };

  // STEP 2: Verify OTP and send reset link
  const handleVerifyOTP = async (otpString) => {
    setError('');
    setSuccess('');

    const otpValue = otpString || otp.join('');
    if (!otpValue || otpValue.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    setIsLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_URL}/auth/forgot-password-step2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp: otpValue }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.locked) {
          // Five wrong codes: this code no longer works; a new one has to be requested.
          setError(data.error || 'Too many wrong codes. Please request a new code.');
          setOtp(['', '', '', '', '', '']);
        } else if (data.invalidOTP) {
          setError('Invalid or expired OTP. Please request a new one.');
          setOtp(['', '', '', '', '', '']);
          otpInputRefs.current[0]?.focus();
        } else {
          setError(data.error || 'Failed to verify OTP');
        }
        setIsLoading(false);
        return;
      }

      setSuccess('OTP verified! Password reset link has been sent to your email.');


      // Optional: Call the parent callback
      if (onSendReset) {
        onSendReset(email);
      }

    } catch (error) {
      console.error('❌ Verify OTP error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = () => {
    setOtp(['', '', '', '', '', '']);
    setShowOtpInput(false);
    setError('');
    setSuccess('');
    handleSendOTP();
  };

  return (
    <>
      <div className="center-wrapper">
        <div className={`container-3 ${showOtpInput ? 'otp-active' : ''}`}>
          <span className="logo"></span>
          <span className="title">INVEXIS</span>
          <div className="form">
            <div className="header" style={{color:"black"}}>Forgot Password</div>
            <div className="instructions">
              {!showOtpInput 
                ? 'Enter your email to receive an OTP for verification.' 
                : 'Enter the 6-digit OTP sent to your email.'}
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {success && (
              <div className="success-message">
                {success}
              </div>
            )}

            <input
              className="input"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              autoCapitalize="none"
              disabled={isLoading || showOtpInput}
            />

            {showOtpInput && (
              <>
                <div className="otp-container">
                  <input
                    className="otp-input" style={{border: '2px solid #000 !important'}}
                    ref={el => otpInputRefs.current[0] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={otp[0] || ''}
                    onChange={(e) => handleOtpChange(0, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(0, e)}
                    onPaste={handleOtpPaste}
                    disabled={isLoading}
                    autoFocus
                  />
                  <input
                    className="otp-input" style={{border: '2px solid #000 !important'}}
                    ref={el => otpInputRefs.current[1] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={otp[1] || ''}
                    onChange={(e) => handleOtpChange(1, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(1, e)}
                    disabled={isLoading}
                  />
                  <input
                    className="otp-input" style={{border: '2px solid #000 !important'}}
                    ref={el => otpInputRefs.current[2] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={otp[2] || ''}
                    onChange={(e) => handleOtpChange(2, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(2, e)}
                    disabled={isLoading}
                  />
                  <input
                    className="otp-input" style={{border: '2px solid #000 !important'}}
                    ref={el => otpInputRefs.current[3] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={otp[3] || ''}
                    onChange={(e) => handleOtpChange(3, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(3, e)}
                    disabled={isLoading}
                  />
                  <input
                    className="otp-input" style={{border: '2px solid #000 !important'}}
                    ref={el => otpInputRefs.current[4] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={otp[4] || ''}
                    onChange={(e) => handleOtpChange(4, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(4, e)}
                    disabled={isLoading}
                  />
                  <input
                    className="otp-input" style={{border: '2px solid #000 !important'}}
                    ref={el => otpInputRefs.current[5] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={otp[5] || ''}
                    onChange={(e) => handleOtpChange(5, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(5, e)}
                    disabled={isLoading}
                  />
                </div>
                <button
                  className="resetButton"
                  onClick={() => handleVerifyOTP()}
                  disabled={isLoading || otp.join('').length !== 6}
                  style={{
                    opacity: otp.join('').length !== 6 ? '0.5' : '1',
                    cursor: otp.join('').length !== 6 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isLoading ? 'Verifying...' : 'Verify OTP'}
                </button>
                <button
                  className="resendButton"
                  onClick={handleResendOTP}
                  disabled={isLoading}
                >
                  Didn't receive OTP? Resend
                </button>
              </>
            )}

            {!showOtpInput && (
              <button
                className="resetButton"
                onClick={handleSendOTP}
                disabled={isLoading}
              >
                {isLoading ? 'Sending OTP...' : 'Send OTP'}
              </button>
            )}

            <button className="backText" onClick={onBack} disabled={isLoading}>
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
      <style>
        {`
          .center-wrapper {
            height: 100vh !important;
            min-height: unset !important;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f7f7f7;
          }
          .container-3 {
            max-width: ${backgroundImage ? '600px' : 'none'};
            width: 100vw;
            height: 100vh !important;
            min-height: unset !important;
            font-family: "Roboto";
            background: ${backgroundImage ? `url('${backgroundImage}')` : "#fff"} no-repeat center center;
            background-size: cover;
            background-position: center;
            // border-radius: 16px;
            position: relative;
            --invexis-input-bg: rgba(255, 255, 255, 0.95);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 0 10px;
            margin: 0 0 3rem 0;
          }
          @media (max-width: 768px) {
            .container-3 {
              background-attachment: scroll;
              background-size: cover;
              overflow: auto;
              justify-content: center;
            }
          }

          .logo {
            font-size: 48px;
            margin-bottom: 8px;
          }

          .title {
            color: black;
            font-size: 36.8px;
            font-weight: bold;
            letter-spacing: 2px;
            margin-bottom: 32px;
          }

          .header {
            font-size: 24px;
            font-weight: bold;
            color: #000;
            margin-bottom: 8px;
            text-align: left;
          }

          .instructions {
            color: #555;
            font-size: 16px;
            margin-bottom: 16px;
            text-align: left;
          }

          .form {
            background-color: rgba(255, 255, 255, 0.95);
            padding: 30px;
            border-radius: 16px;
            width: 100%;
            max-width: 400px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            align-items: stretch;
            box-shadow: 0 8px 32px rgba(0,0,0,0.2);
          }

          .input {
            color: black;
            height: 2.8rem;
            border: 2px solid #000 !important;
            border-radius: 8px;
            margin-bottom: 3px;
            padding: 0 12px;
            background-color: rgba(255, 255, 255, 0.9);
            font-size: 16px;
            width: 100%;
            box-sizing: border-box;
            outline: none;
            transition: none !important;
            transform: none !important;
          }

          .input:-webkit-autofill,
          .input:-webkit-autofill:hover,
          .input:-webkit-autofill:focus,
          .input:-webkit-autofill:active {
            background-color: rgba(255, 255, 255, 0.9) !important;
            -webkit-box-shadow: 0 0 0 1000px rgba(255, 255, 255, 0.9) inset !important;
            box-shadow: 0 0 0 1000px rgba(255, 255, 255, 0.9) inset !important;
            -webkit-text-fill-color: #000 !important;
            caret-color: #000 !important;
            border: 2px solid #000 !important;
            transition: background-color 999999s ease-out 0s !important;
          }

          .input:-moz-autofill {
            box-shadow: 0 0 0 1000px rgba(255, 255, 255, 0.9) inset !important;
            background-color: rgba(255, 255, 255, 0.9) !important;
            color: #000 !important;
          }

          .input:focus {
            border: 2px solid #000 !important;
            outline: none;
            transition: none !important;
            transform: none !important;
          }

          .input:disabled {
            background-color: #f0f0f0;
            cursor: not-allowed;
            opacity: 0.6;
          }

          .input::placeholder {
            color: #black;
          }

          .error-message {
            background-color: #fee;
            color: #c33;
            padding: 12px;
            border-radius: 8px;
            margin-bottom: 12px;
            text-align: center;
            font-size: 14px;
          }

          .success-message {
            background-color: #efe;
            color: #3a3;
            padding: 12px;
            border-radius: 8px;
            margin-bottom: 8px;
            text-align: center;
            font-size: 14px;
          }

          .resetButton {
            background-color: #000;
            color: #fff;
            padding: 14px 0;
            border-radius: 8px;
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            cursor: pointer;
            border: none;
            margin: 1rem 0;
          }

          .resetButton:hover:not(:disabled) {
            background-color: #000;
          }

          .resetButton:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .backText {
            color: #222;
            text-align: center;
            margin-top: 8px;
            text-decoration: underline;
            font-size: 15px;
            background: none;
            border: none;
            cursor: pointer;
          }

          .backText:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .otp-container {
            display: flex !important;
            justify-content: space-around;
            align-items: center;
            gap: 8px;
            margin: 16px 0;
            width: 100%;
            box-sizing: border-box;
            flex-wrap: nowrap;
          }

          .otp-input {
            width: 44px !important;
            height: 44px !important;
            min-width: 44px !important;
            min-height: 44px !important;
            max-width: 44px !important;
            max-height: 44px !important;
            border-radius: 0.5rem !important;
            text-align: center !important;
            font-size: 20px !important;
            font-weight: bold !important;
            color: #000 !important;
            background-color: #fff !important;
            transition: none !important;
            transform: none !important;
            font-family: "Roboto" !important;
            box-sizing: border-box !important;
            flex-shrink: 0 !important;
            display: inline-block !important;
            padding: 0 !important;
            margin: 0 !important;
            line-height: 44px !important;
          }

          .otp-input:focus {
            border-color: #000 !important;
            box-shadow: none !important;
            outline: none !important;
            transform: none !important;
          }

          /* Override global smoothTransitions.css for auth screen */
          .container-3 input,
          .container-3 button {
            transition: none !important;
          }
          .container-3 input:focus,
          .container-3 input:active,
          .container-3 button:active {
            transform: none !important;
          }

          .otp-input:disabled {
            background-color: #f5f5f5;
            cursor: not-allowed;
            opacity: 0.6;
          }

          /* Strong overrides to beat global input resets */
          .form .otp-container input.otp-input,
          .form .otp-container input.otp-input[type="text"],
          .form .otp-container input.otp-input[type="search"] {
            border: 2px solid #000 !important;
            background-color: #fff !important;
            box-shadow: none !important;
            outline: none !important;
          }

          .resendButton {
            background: transparent;
            color: #6366f1;
            border: none;
            cursor: pointer;
            font-size: 14px;
            text-decoration: underline;
            margin: 8px 0 16px 0;
            padding: 8px;
          }

          .resendButton:hover:not(:disabled) {
            color: #4f46e5;
          }

          .resendButton:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          /* Responsive design for tablets */
          @media (max-width: 768px) {
            .otp-container {
              gap: 6px;
              padding: 0 4px;
            }

            .otp-input {
              width: 40px !important;
              height: 40px !important;
              font-size: 18px !important;
              border-radius: 8px !important;
              line-height: 40px !important;
            }

            .form {
              max-width: 90%;
              padding: 24px 20px;
            }
          }

          /* Responsive design for mobile phones */
          @media (max-width: 480px) {
            .container-3 {
              justify-content: center;
              padding-top: 20px;
              padding-left: 10px;
              padding-right: 10px;
            }

            /* Only when OTP inputs are shown (image-1), move container slightly up */
            .container-3.otp-active {
              padding-top: 10px;
            }

            .title {
              margin-bottom: 20px;
            }

            .form {
              margin-top: 8px;
            }
            .otp-container {
              gap: 4px;
              padding: 0;
            }

            .otp-input {
              width: 36px !important;
              height: 36px !important;
              font-size: 16px !important;
              border-radius: 8px !important;
              line-height: 36px !important;
            }

            .form {
              max-width: 95%;
              padding: 20px 16px;
            }

            .header {
              font-size: 20px;
            }

            .instructions {
              font-size: 14px;
            }
          }

          /* Responsive design for very small phones */
          @media (max-width: 360px) {
            .container-3 {
              justify-content: center;
              padding-top: 20px;
              padding-left: 10px;
              padding-right: 10px;
            }

            .title {
              margin-bottom: 16px;
            }
            .otp-container {
              gap: 3px;
            }

            .otp-input {
              width: 32px !important;
              height: 32px !important;
              font-size: 14px !important;
              border-radius: 6px !important;
              line-height: 32px !important;
            }
          }

          .bottomLinks {
            display: flex;
            justify-content: space-between;
            width: 80%;
            position: absolute;
            bottom: 24px;
          }

          .link {
            color: #fff;
            font-size: 13px;
            text-decoration: underline;
          }
        `}
      </style>
    </>
  );
}